/**
 * The mega menu flyouts and the mobile menu panels are not part of the initial page HTML.
 * They are rendered on demand through the Section Rendering API using the empty
 * `search.renderer` template (`/search?sections=<section id>&view=renderer&q=mobile|desktop`),
 * so each menu only downloads its own markup. Requests are shared per URL.
 * @type {Map<string, Promise<Document>>}
 */
const rendererDocuments = new Map();

/** The rendered markup is kept in sessionStorage for this long so later pages open the menus instantly. */
const RENDERER_CACHE_TTL = 30 * 60 * 1000;

const DESKTOP_MEDIA_QUERY = '(min-width: 64rem)';

/**
 * Reads cached renderer markup from sessionStorage.
 * @param {string} key
 * @returns {string|null}
 */
function readRendererCache(key) {
  try {
    const entry = JSON.parse(sessionStorage.getItem(key));
    if (entry && Date.now() - entry.time < RENDERER_CACHE_TTL) return entry.html;
  } catch {
    // sessionStorage unavailable or entry unreadable
  }
  return null;
}

/**
 * Writes renderer markup to sessionStorage.
 * @param {string} key
 * @param {string} html
 */
function writeRendererCache(key, html) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), html }));
  } catch {
    // sessionStorage full or unavailable
  }
}

/**
 * Fetches the header section rendered with the `renderer` view and parses it.
 * @param {string} url - The section rendering URL
 * @param {string} sectionId - The section ID used as key in the JSON response
 * @returns {Promise<Document>}
 */
function fetchRendererDocument(url, sectionId) {
  if (!rendererDocuments.has(url)) {
    const cacheKey = `apc-header-renderer:${window.Shopify?.theme?.id ?? ''}:${url}`;
    const cached = readRendererCache(cacheKey);

    const htmlPromise = cached
      ? Promise.resolve(cached)
      : fetch(url, { priority: 'high' })
          .then((response) => response.json())
          .then((data) => {
            const html = data[sectionId] ?? Object.values(data)[0] ?? '';
            if (html) writeRendererCache(cacheKey, html);
            return html;
          });

    const promise = htmlPromise
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch((error) => {
        rendererDocuments.delete(url);
        throw error;
      });
    rendererDocuments.set(url, promise);
  }

  return rendererDocuments.get(url);
}

class ApcMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.menuItems = this.querySelectorAll('[data-mega-menu]');
    this.activeItem = null;
    this.flyoutsLoaded = false;
    this.init();
  }

  init() {
    for (const item of this.menuItems) {
      // Flyouts already in the DOM (theme editor) can be initialised right away
      if (item.querySelector('.apc-header__megamenu, .apc-header__quicklinks')) {
        this.flyoutsLoaded = true;
        this.initFlyout(item);
      }

      item.addEventListener('mouseenter', () => {
        this.openItem(item);
      });

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeItem(item);
          const menuLink = item.querySelector('.apc-header__menu-link');
          if (menuLink) menuLink.focus();
        }
      });
    }

    const simpleItems = this.querySelectorAll('.apc-header__menu-item:not([data-mega-menu]), .apc-header__menu--secondary li');
    for (const item of simpleItems) {
      item.addEventListener('mouseenter', () => {
        if (this.activeItem) this.closeItem(this.activeItem);
      });
    }

    this.addEventListener('mouseleave', () => {
      if (this.activeItem) this.closeItem(this.activeItem);
    });

    // Desktop visitors are very likely to open a flyout, so fetch them right away instead of
    // waiting for the first hover. Smaller viewports only fetch when the menu bar is used.
    if (this.menuItems.length > 0 && window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
      this.loadFlyouts();
    } else {
      for (const type of ['mouseenter', 'focusin', 'touchstart']) {
        this.addEventListener(type, () => this.loadFlyouts(), { passive: true, once: true });
      }
    }
  }

  loadFlyouts() {
    if (this.flyoutsLoaded || !this.dataset.rendererUrl) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = fetchRendererDocument(this.dataset.rendererUrl, this.dataset.sectionId)
      .then((doc) => {
        for (const item of this.menuItems) {
          const source = doc.querySelector(`[data-mega-menu][data-menu-index="${item.dataset.menuIndex}"]`);
          const flyout = source?.querySelector('.apc-header__megamenu, .apc-header__quicklinks');
          if (!flyout || item.querySelector('.apc-header__megamenu, .apc-header__quicklinks')) continue;

          item.appendChild(flyout);
          this.initFlyout(item);
        }

        this.flyoutsLoaded = true;
        if (this.activeItem) this.resetChildren(this.activeItem);
      })
      .catch(() => {
        this.loadPromise = null;
      });

    return this.loadPromise;
  }

  initFlyout(item) {
    const childLinks = item.querySelectorAll('.apc-header__megamenu-child-link');
    const grandchildPanels = item.querySelectorAll('.apc-header__megamenu-grandchildren');

    for (const link of childLinks) {
      link.addEventListener('mouseenter', () => {
        this.activateChild(link, childLinks, grandchildPanels);
      });

      link.addEventListener('focus', () => {
        this.activateChild(link, childLinks, grandchildPanels);
      });

      // Tab from child link → focus first grandchild link in visible panel
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          const visiblePanel = item.querySelector('.apc-header__megamenu-grandchildren:not([hidden])');
          const firstGrandchild = visiblePanel?.querySelector('a');
          if (firstGrandchild) {
            e.preventDefault();
            firstGrandchild.focus();
          }
        }
      });
    }

    // Tab from last grandchild link → focus next child link
    for (const panel of grandchildPanels) {
      const panelLinks = panel.querySelectorAll('a');
      if (panelLinks.length === 0) continue;

      const lastLink = panelLinks[panelLinks.length - 1];
      lastLink.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          const index = Number(panel.dataset.grandchildrenIndex);
          const childArray = Array.from(childLinks);
          const currentChild = childArray.find((c) => c.dataset.childIndex === String(index));
          const currentIdx = childArray.indexOf(currentChild);
          const nextChild = childArray[currentIdx + 1];
          if (nextChild) {
            e.preventDefault();
            nextChild.focus();
          }
        }
      });

      // Shift+Tab from first grandchild → focus back to its child link
      const firstLink = panelLinks[0];
      firstLink.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          const index = panel.dataset.grandchildrenIndex;
          const childLink = item.querySelector(`.apc-header__megamenu-child-link[data-child-index="${index}"]`);
          if (childLink) {
            e.preventDefault();
            childLink.focus();
          }
        }
      });
    }
  }

  openItem(item) {
    if (this.activeItem && this.activeItem !== item) {
      this.closeItem(this.activeItem);
    }

    this.activeItem = item;
    item.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.loadFlyouts();
    this.resetChildren(item);
    const menuLink = item.querySelector('.apc-header__menu-link');
    if (menuLink) menuLink.setAttribute('aria-expanded', 'true');
  }

  resetChildren(item) {
    const childLinks = item.querySelectorAll('.apc-header__megamenu-child-link');
    const panels = item.querySelectorAll('.apc-header__megamenu-grandchildren');
    if (childLinks.length > 0) {
      this.activateChild(childLinks[0], childLinks, panels);
    }
  }

  closeItem(item) {
    item.classList.remove('is-open');
    const menuLink = item.querySelector('.apc-header__menu-link');
    if (menuLink) menuLink.setAttribute('aria-expanded', 'false');
    if (this.activeItem === item) {
      this.activeItem = null;
      document.body.style.overflow = '';
    }
  }

  activateChild(activeLink, allLinks, panels) {
    const index = activeLink.dataset.childIndex;

    for (const link of allLinks) {
      link.classList.toggle('is-active', link === activeLink);
    }

    for (const panel of panels) {
      panel.hidden = panel.dataset.grandchildrenIndex !== index;
    }
  }
}

customElements.define('apc-mega-menu', ApcMegaMenu);

class ApcMobileMenu extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('.apc-mobile-menu__dialog');
    this.closeBtn = this.querySelector('.apc-mobile-menu__close');
    this.panels = this.querySelector('.apc-mobile-menu__panels');
    this.activePanel = this.querySelector('.apc-mobile-menu__panel.is-active');
    this.history = ['root'];
    this.isAnimating = false;
    this.loadPromise = null;
    this.bindEvents();
  }

  bindEvents() {
    this.closeBtn.addEventListener('click', () => this.close());

    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    this.addEventListener('click', (e) => {
      const navBtn = e.target.closest('[data-target]');
      if (navBtn) {
        this.navigateTo(navBtn.dataset.target);
        return;
      }
      const gotoBtn = e.target.closest('[data-goto]');
      if (gotoBtn) {
        this.navigateToAncestor(gotoBtn.dataset.goto);
      }
    });

    const toggle = document.querySelector(`[aria-controls="${this.id}"]`);
    if (toggle) {
      toggle.addEventListener('click', () => this.open());
      // Start fetching the menu markup as soon as the user reaches for the toggle
      for (const type of ['pointerenter', 'touchstart', 'focus']) {
        toggle.addEventListener(type, () => this.loadPanels(), { passive: true, once: true });
      }
    }
  }

  async open() {
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    await this.loadPanels();
    this.resetToRoot();
  }

  loadPanels() {
    if (!this.panels || this.panels.children.length > 0 || !this.dataset.rendererUrl) {
      return Promise.resolve();
    }
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = fetchRendererDocument(this.dataset.rendererUrl, this.dataset.sectionId)
      .then((doc) => {
        const panels = doc.querySelector('.apc-mobile-menu__panels');
        if (panels) this.panels.innerHTML = panels.innerHTML;
      })
      .catch(() => {
        this.loadPromise = null;
      });

    return this.loadPromise;
  }

  close() {
    this.dialog.classList.add('is-closing');
    this.dialog.addEventListener(
      'animationend',
      () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        document.body.style.overflow = '';
      },
      { once: true }
    );
    const toggle = document.querySelector(`[aria-controls="${this.id}"]`);
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  resetToRoot() {
    for (const panel of this.querySelectorAll('.apc-mobile-menu__panel')) {
      panel.classList.remove('is-active', 'is-exit-left', 'is-exit-right', 'is-enter-left', 'is-enter-right');
    }
    const root = this.querySelector('[data-panel="root"]');
    if (root) {
      root.classList.add('is-active');
      this.activePanel = root;
    }
    this.history = ['root'];
  }

  navigateTo(panelId) {
    if (this.isAnimating) return;
    const next = this.querySelector(`[data-panel="${panelId}"]`);
    if (!next || !this.activePanel) return;

    this.isAnimating = true;
    this.history.push(panelId);

    const current = this.activePanel;
    current.classList.remove('is-active');
    current.classList.add('is-exit-left');

    next.classList.add('is-active', 'is-enter-right');
    next.scrollTop = 0;

    next.addEventListener(
      'animationend',
      () => {
        current.classList.remove('is-exit-left');
        next.classList.remove('is-enter-right');
        this.isAnimating = false;
      },
      { once: true }
    );

    this.activePanel = next;
  }

  navigateToAncestor(panelId) {
    if (this.isAnimating) return;
    const target = this.querySelector(`[data-panel="${panelId}"]`);
    if (!target || !this.activePanel) return;

    this.isAnimating = true;

    while (this.history.length > 1 && this.history[this.history.length - 1] !== panelId) {
      this.history.pop();
    }
    if (this.history[this.history.length - 1] !== panelId) {
      this.history = [panelId];
    }

    const current = this.activePanel;
    current.classList.remove('is-active');
    current.classList.add('is-exit-right');

    target.classList.add('is-active', 'is-enter-left');

    target.addEventListener(
      'animationend',
      () => {
        current.classList.remove('is-exit-right');
        target.classList.remove('is-enter-left');
        this.isAnimating = false;
      },
      { once: true }
    );

    this.activePanel = target;
  }
}

customElements.define('apc-mobile-menu', ApcMobileMenu);
